import { useLoader } from '@react-three/fiber';
import { type FC, memo, useContext, useEffect, useRef } from 'react';
import type { DataFormat } from '../types';
import { type BinPoints, BinPointsLoader } from './Loaders/BinPointsLoader';
import { FileLoaderOptionsContext } from './FileLoaderOptionsContext';
import { OctreeNode } from './Octree/OctreeNode';
import { PointCloudNodeFromData, type PointCloudNodeFromDataProps } from './PointCloudNodeFromData';

export interface PointCloudNodeProps extends Omit<PointCloudNodeFromDataProps, 'binPoints'> {
  requestHeader?: { [header: string]: string };
  dataFormat?: DataFormat;
  onViewerObjectReady: (isReady: boolean) => void;
  octreeNode?: OctreeNode;
  onUnmount?: (octreeNode?: OctreeNode) => void;
  onNodeFileLoaded?: (octreeNode?: OctreeNode) => void;
}

export const PointCloudNode: FC<PointCloudNodeProps> = memo(
  ({
    name,
    url,
    requestHeader,
    dataFormat,
    material,
    showInSphericalView,
    position,
    rotation,
    isVisible,
    getPointCloudMaterial,
    onViewerObjectReady,
    onUnmount,
    onNodeFileLoaded,
    octreeNode,
  }) => {
    const fileLoaderOptions = useContext(FileLoaderOptionsContext);

    const { positionData, attributes }: BinPoints = useLoader(BinPointsLoader, url, (loader) => {
      loader.setWithCredentials(fileLoaderOptions.withCredentials);
      if (dataFormat) {
        loader.setDataFormat(url, dataFormat);
      }
      if (requestHeader) {
        loader.setRequestHeader(requestHeader);
      }
    });

    useEffect(() => {
      return () => {
        useLoader.clear(BinPointsLoader, url);
      };
    }, [url]);

    const isLoaded = useRef<boolean>(false);

    useEffect(() => {
      if (!isLoaded.current && positionData) {
        isLoaded.current = true;
        onNodeFileLoaded?.(octreeNode);
      }
    }, [positionData, onNodeFileLoaded, octreeNode]);

    useEffect(() => {
      return () => {
        if (onUnmount) {
          onUnmount(octreeNode);
        }
      };
    }, [onUnmount, octreeNode]);

    useEffect(() => {
      onViewerObjectReady(true);
      return () => {
        onViewerObjectReady(false);
      };
    }, [onViewerObjectReady]);

    return (
      <PointCloudNodeFromData
        octreeId={octreeNode?.octreeId ?? url}
        binPoints={{ positionData, attributes }}
        name={name}
        url={url}
        key={url}
        material={material}
        showInSphericalView={showInSphericalView}
        position={position}
        rotation={rotation}
        isVisible={isVisible}
        getPointCloudMaterial={getPointCloudMaterial}
      />
    );
  }
);
PointCloudNode.displayName = 'PointCloudNode';
